'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { X, Mail, Gift, CheckCircle, Loader2, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import styles from './SoftGateModal.module.css';

interface SoftGateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (email: string) => void;
  hiddenFindingsCount: number;
  totalFindings: number;
  reportId: string;
  source?: string;
}

export function SoftGateModal({
  isOpen,
  onClose,
  onSuccess,
  hiddenFindingsCount,
  totalFindings,
  reportId,
  source = 'soft_gate_audit',
}: SoftGateModalProps) {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Focus trap refs
  const modalRef = useRef<HTMLDivElement>(null);
  const firstFocusableRef = useRef<HTMLButtonElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);

  // Focus trap implementation
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
      return;
    }

    if (e.key !== 'Tab') return;

    const focusableElements = modalRef.current?.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (!focusableElements || focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (e.shiftKey && document.activeElement === firstElement) {
      e.preventDefault();
      lastElement.focus();
    } else if (!e.shiftKey && document.activeElement === lastElement) {
      e.preventDefault();
      firstElement.focus();
    }
  }, [onClose]);

  // Modal focus management
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        emailInputRef.current?.focus();
      }, 50);

      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';

      return () => {
        clearTimeout(timer);
        document.removeEventListener('keydown', handleKeyDown);
        document.body.style.overflow = '';
      };
    } else {
      document.body.style.overflow = '';
    }
  }, [isOpen, handleKeyDown]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      // Subscribe to newsletter with source tracking
      const response = await fetch('/api/newsletter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          source,
          reportId, // Track which audit triggered the signup
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to subscribe. Please try again.');
        return;
      }

      // Success - show confirmation and unlock findings
      setSuccess(true);

      // Delay to show success message before closing
      setTimeout(() => {
        onSuccess(email);
      }, 1500);
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className={styles.overlay}
      onClick={onClose}
      role="presentation"
    >
      <div
        ref={modalRef}
        className={styles.modal}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="soft-gate-title"
        aria-describedby="soft-gate-description"
      >
        {/* Close button */}
        <button
          ref={firstFocusableRef}
          className={styles.closeButton}
          onClick={onClose}
          aria-label="Close dialog"
        >
          <X size={20} />
        </button>

        {/* Decorative gradient orb */}
        <div className={styles.gradientOrb} aria-hidden="true" />

        {success ? (
          /* Success State */
          <div className={styles.successContent}>
            <div className={styles.successIcon}>
              <CheckCircle size={48} />
            </div>
            <h2 className={styles.successTitle}>Check Your Email!</h2>
            <p className={styles.successMessage}>
              We sent a verification link to <strong>{email}</strong>.
              Click it to unlock your additional findings.
            </p>
            <p className={styles.successNote}>
              You&apos;ll also receive our 6-part UX Laws series -
              essential principles every designer should know.
            </p>
          </div>
        ) : (
          /* Form State */
          <>
            {/* Header */}
            <div className={styles.header}>
              <div className={styles.iconWrapper}>
                <Gift size={32} className={styles.giftIcon} />
                <Sparkles size={16} className={styles.sparkleIcon} aria-hidden="true" />
              </div>
              <h2 id="soft-gate-title" className={styles.title}>
                Unlock {hiddenFindingsCount} More Findings
              </h2>
              <p id="soft-gate-description" className={styles.description}>
                Enter your email to reveal additional UX issues - completely free.
                Plus, get our 6-part UX Laws email series.
              </p>
            </div>

            {/* Value proposition */}
            <div className={styles.valueList}>
              <div className={styles.valueItem}>
                <CheckCircle size={16} className={styles.checkIcon} />
                <span>{hiddenFindingsCount} additional findings unlocked</span>
              </div>
              <div className={styles.valueItem}>
                <CheckCircle size={16} className={styles.checkIcon} />
                <span>6-part UX Laws email series (Hick&apos;s, Fitts&apos;s, Jakob&apos;s...)</span>
              </div>
              <div className={styles.valueItem}>
                <CheckCircle size={16} className={styles.checkIcon} />
                <span>No spam, unsubscribe anytime</span>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.inputWrapper}>
                <Mail size={18} className={styles.inputIcon} aria-hidden="true" />
                <input
                  ref={emailInputRef}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  autoComplete="email"
                  className={styles.emailInput}
                  disabled={isLoading}
                  aria-label="Email address"
                />
              </div>

              {error && (
                <p className={styles.errorMessage} role="alert">
                  {error}
                </p>
              )}

              <Button
                type="submit"
                size="lg"
                disabled={isLoading || !email}
                className={styles.submitButton}
              >
                {isLoading ? (
                  <>
                    <Loader2 size={18} className={styles.spinner} aria-hidden="true" />
                    Sending...
                  </>
                ) : (
                  <>
                    Unlock Free Findings
                  </>
                )}
              </Button>
            </form>

            {/* Skip link */}
            <button
              type="button"
              className={styles.skipLink}
              onClick={onClose}
            >
              No thanks, I&apos;ll stick with 8 findings
            </button>

            {/* Remaining findings teaser */}
            <div className={styles.remainingTeaser}>
              <p className={styles.remainingText}>
                Still want more? After unlocking these {hiddenFindingsCount} findings,
                upgrade to our <strong>Full UX Report ($99)</strong> for
                all {totalFindings} findings + detailed recommendations.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
